import { FC, lazy, Suspense, useEffect, useState } from "react"
import { matchPath, Outlet, useLocation } from "react-router-dom"

import { UAlert, UButton, ULoading, UText } from "@/components"

import { CssBaseline, Grid } from "../../components/mui.components"
import { useAppDispatch, useAppSelector } from "../../core/app/hooks"
import { RootState } from "../../core/app/store"
import { isTokenValid } from "../../core/config/okta.config"
import { useMyNavigation } from "../../hooks/useMyNavigation"

import Footer from "./Footer/Footer"
import Header from "./Header/Header"
import CsHeader from "./Header/CsHeader"
import { closeAlert } from "@/core/app/slices/alert/alertSlice"

const AlertBanner = lazy(
  () => import("../../components/AlertBanner/AlertBanner"),
)

const MainLayout: FC = () => {
  const location = useLocation()
  const navigate = useMyNavigation()
  const isLogin = isTokenValid()
  const { user, systemBannerMessage, marketBannerMessage } = useAppSelector(
    (state: RootState) => state.userService,
  )
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const { alertMsg, isError, severity, btnText, btnAction, timeout, horizontalPosition } = useAppSelector(
    (state: RootState) => state.alert,
  )
  
  // Check if this page was opened in a new tab (window.opener exists when opened via window.open())
  // Banner is hidden for tabs opened via Patients List or Patient Overview,
  // based on the updated behavior of opening cases in a new tab and creating cases in new tab for selfplan.
  const isOpenedInNewTab = !!window.opener

  // Helper function to check if current path matches a given pattern
  const isPathMatching = (pattern: string) => matchPath(pattern, location.pathname)

  const shouldShowHeader = () => {
    // If user can view all organizations
    if (user.isAllowToViewAllOrg) {
      // Show header if:
      // 1. Cs User is viewing a different organization than their own, AND
      // 2. Not on organization page, AND
      const isViewingDifferentOrg = user.current_orgId !== user.my_orgId
      const isNotOrgListPage = !isPathMatching("/organization")

      return isViewingDifferentOrg && isNotOrgListPage
    } else {
      // If user is not a Cs user, always show header
      return true
    }
  }

  // Check if we're on a clinical case page (regardless of org context)
  const isOnClinicalPage = isPathMatching("/clinical/patient/:patientId/case/:caseId/*") || isPathMatching("/org/:orgId/clinical/patient/:patientId/case/:caseId/*")

  // Check if we're on the OAuth callback page (lightweight page — no chrome needed)
  const isOnOAuthCallbackPage = !!(isPathMatching("/integrations/callback") || isPathMatching("/org/:orgId/integrations/callback"))

  // Final decision: show header if conditions are met AND not on clinical case page
  const showHeader = shouldShowHeader() && !isOnClinicalPage && !isOnOAuthCallbackPage
  const { isLoggingOut } = useAppSelector((state: RootState) => state.userService)
  //if user is logging out , wait for okta redirection to login page

  useEffect(() => {
    const destinationPath = `${
      location.pathname + location.search + location.hash
    }`
    if (isLogin) {
      if (location.pathname.includes("/auth") || location.pathname === "/") {
        navigate(user.isAllowToViewAllOrg ? "/organization" : "/patients")
      }
    } else if(!isLoggingOut) {
      if (
        !location.pathname.includes("/auth") &&
        destinationPath != "/" &&
        destinationPath != "/patients" &&
        destinationPath != "/organization"
      ) {
        navigate(
          `/auth/login?redirectTo=${encodeURIComponent(destinationPath)}`,
          {
            replace: true,
          },
        )
      } else {
        navigate(`/auth/login`, {
          replace: true,
        })
      }
    }
  }, [location, user, isLoggingOut])

    useEffect(() => {
      if (alertMsg) {
        setShowAlert(true)
      }
    }, [alertMsg])

    const closeAlertToast = () => {
      setShowAlert(false)
      dispatch(closeAlert())
    }

  return (
    <>
      {!isLogin && isLoggingOut ? (
        <ULoading isLoading={true} />
      ) : (
        isLogin && (
        <>
          <CssBaseline />
          { !isOpenedInNewTab && systemBannerMessage?.id && (
            <Suspense fallback={<ULoading isLoading={true} />}>
              <AlertBanner
                bannerInfo={systemBannerMessage}
                isMarketingBanner={false}
              />
            </Suspense>
          )}
          {user.isAllowToViewAllOrg && <CsHeader />}
          {/* Keep Header mounted but hidden with CSS to prevent remounting.
              Remounting causes all refs/state to reset, triggering unnecessary API calls in Header's useEffect.
              Using display: none preserves component state during navigation. */}
          <div style={{ display: showHeader ? 'block' : 'none' }}>
            <Header />
          </div>

          <Grid
            sx={{
              background: "#F7F8F9",
              flexGrow: 1,
            }}
            container
            component={"main"}
          >
            <Outlet />
          </Grid>

          {alertMsg && showAlert && (
            <UAlert
              horizontalPosition={horizontalPosition}
              alertContent={
                <>
                  <UText
                    sxProp={{
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "143%",
                    }}
                  >
                    {alertMsg}
                  </UText>
                  {!!btnText && !!btnAction && (
                    <UButton
                      sxProp={{
                        width: "120px",
                        height: "30px",
                        color: "#ffffff",
                        border: "1px solid #ffffff",
                        borderRadius: "4px",
                        fontSize: "13px",
                        marginLeft: 2,
                        padding: "4px 10px",
                      }}
                      onClickHandler={btnAction}
                      btnText={btnText}
                    />
                  )}
                </>
              }
              variant={"filled"}
              severity={severity || (isError ? "error" : "success")}
              alertProps={{ paddingBottom: "10px" }}
              showAlert={showAlert}
              onClickHandler={closeAlertToast}
              timeout={timeout}
            ></UAlert>
          )}
          { !isOpenedInNewTab && !location.pathname.includes("/clinical/") && !isOnOAuthCallbackPage && (
            <>
              <Footer />
              {marketBannerMessage?.id && (
                <Suspense fallback={<ULoading isLoading={true} />}>
                  <AlertBanner
                    bannerInfo={marketBannerMessage}
                    isMarketingBanner={true}
                  />
                </Suspense>
              )}
            </>
          )}
        </>
        )
      )}
    </>
  )
}

export default MainLayout
